// Pure helpers for the iOS-host issue reporter (`hostIssueReporter.js`): turn
// console arguments, errors and page state into the bounded report the
// `reportClientIssue` Cloud Function logs. No DOM, no Firebase — so the
// shaping and, above all, the secret redaction are testable on their own.

export const REPORT_LIMITS = {
  text: 2000,
  crumb: 300,
  breadcrumbs: 25,
};

// Errors that are noise in any WebView and say nothing about our code.
const IGNORED_MESSAGES = [/ResizeObserver loop/i, /^Script error\.?$/i];

// The iOS handover puts a Firebase ID token in the query string
// (`?token=…&uid=…&source=ios`), and anything that logs a URL copies it. A
// report must never carry one: redact token-named parameters and any
// JWT-shaped string wherever it appears.
const TOKEN_PARAM =
  /([?&#](?:token|idToken|customToken|access_token)=)[^&#\s"']+/gi;
const JWT = /\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}/g;

export function redactSecrets(text) {
  if (typeof text !== 'string') return text;
  return text.replace(TOKEN_PARAM, '$1…').replace(JWT, '<jwt>');
}

export function clip(text, max) {
  if (typeof text !== 'string') return text;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** The path only: a query string or hash may carry the handover token. */
export function pathOnly(href) {
  if (typeof href !== 'string' || !href) return null;
  try {
    return new URL(href, 'http://host.invalid').pathname;
  } catch {
    return href.split(/[?#]/)[0];
  }
}

function describeValue(value) {
  if (value instanceof Error) {
    return [value.code, value.message].filter(Boolean).join(' ') || value.name;
  }
  if (value === null || value === undefined) return String(value);
  if (typeof value !== 'object') return String(value);
  // Plain data only: stringifying a scene graph or a DOM node would be slow
  // and useless, and a render loop can log one every frame.
  const proto = Object.getPrototypeOf(value);
  if (!Array.isArray(value) && proto !== Object.prototype && proto !== null) {
    return Object.prototype.toString.call(value);
  }
  try {
    return clip(JSON.stringify(value), REPORT_LIMITS.crumb);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/** One line from console arguments, redacted and bounded. */
export function formatConsoleArgs(args, max = REPORT_LIMITS.crumb) {
  const text = Array.from(args ?? [], describeValue).join(' ');
  return clip(redactSecrets(text), max);
}

/** The first Error among console arguments / an event's reason, if any. */
export function firstError(values) {
  return Array.from(values ?? []).find((v) => v instanceof Error) ?? null;
}

export function isIgnoredMessage(message) {
  return (
    typeof message === 'string' &&
    IGNORED_MESSAGES.some((pattern) => pattern.test(message))
  );
}

/**
 * The payload sent to `reportClientIssue`. Every string is redacted and
 * clipped here as well as on the server, so a secret never leaves the page.
 */
export function buildIssuePayload({
  kind,
  message,
  error,
  context,
  breadcrumbs,
  href,
  webBuild,
  userAgent,
  hostDocuments,
  pageLoad,
}) {
  const stack = error?.stack
    ? clip(redactSecrets(error.stack), REPORT_LIMITS.text)
    : null;
  return {
    kind,
    message: clip(redactSecrets(message), REPORT_LIMITS.text),
    stack,
    path: pathOnly(href),
    webBuild: webBuild ?? null,
    userAgent: userAgent ?? null,
    hostDocuments: Boolean(hostDocuments),
    pageLoad: pageLoad ?? null,
    context: context ?? null,
    breadcrumbs: (breadcrumbs ?? []).slice(-REPORT_LIMITS.breadcrumbs),
  };
}
